import {
    ActionButton,
    ConstrainMode,
    IColumn,
    IDetailsRowProps,
    IShimmeredDetailsListProps,
    Selection,
    ShimmeredDetailsList as FabricShimmeredDetailsList,
    Stack,
    IObjectWithKey,
    SelectionMode,
    IDetailsListStyles,
    mergeStyleSets,
    DetailsListLayoutMode
} from "@fluentui/react";
import React, { useEffect, useRef, useState } from "react";

import { ensure } from "../../utils/ensure/ensure";
import { Theme } from "../theme/Theme";
import { getLayoutMode, LayoutMode } from "./LayoutMode";

export type Column = IColumn;

export type ShimmeredDetailsListProps = Pick<
    IShimmeredDetailsListProps,
    | "items"
    | "enableShimmer"
    | "shimmerLines"
    | "onRenderItemColumn"
    | "onColumnHeaderClick"
    | "onRenderRow"
    | "onShouldVirtualize"
    | "isHeaderVisible"
> & {
    columns: Column[];
    selectedItems?: Item[];
    enableRowShading?: boolean;
    maintainSelection?: boolean;
    layoutMode?: LayoutMode;
    onRenderExpandedRow?: (rowProps?: IDetailsRowProps) => React.ReactNode;
    onSelectionChange?(selectedItems: Item[]): void;
    isItemSelectable?(item: Item): boolean;

    selectionMode?: SelectionMode;
    testId?: string;
    isStickyModeEnabled?: boolean;
};

type Item = IShimmeredDetailsListProps["items"][number];

type RenderFunction<T = IDetailsRowProps> = (
    props?: T,
    defaultRender?: (props: T) => JSX.Element | null
) => JSX.Element | null;

export const ShimmeredDetailsList: React.FC<ShimmeredDetailsListProps> = ({
    items,
    columns,
    layoutMode,
    selectedItems: selectedKeys,
    shimmerLines,
    enableShimmer,
    maintainSelection,
    enableRowShading,
    isHeaderVisible,
    selectionMode,
    onColumnHeaderClick,
    onRenderItemColumn,
    onRenderRow,
    onShouldVirtualize,
    onRenderExpandedRow,
    onSelectionChange,
    isItemSelectable,
    testId,
    isStickyModeEnabled
}: ShimmeredDetailsListProps) => {
    const [expandedItemIds, setExpandedItemIds] = useState<string[]>([]);
    const itemsRef = useRef<IObjectWithKey[]>(items);

    const [selection, setSelection] = useState<Selection>(
        new Selection({
            selectionMode: onSelectionChange ? selectionMode ?? SelectionMode.multiple : SelectionMode.none,
            canSelectItem: isItemSelectable,
            onSelectionChanged: () => {
                if (onSelectionChange) {
                    onSelectionChange(selection.getSelection());
                }
            }
        })
    );

    const renderRow = (props?: IDetailsRowProps, defaultRender?: RenderFunction): JSX.Element | null => {
        ensure(props);
        ensure(defaultRender);

        if (onRenderRow) {
            return onRenderRow({ ...props, styles: { root: { userSelect: "auto" } } }, defaultRender);
        }

        return enableRowShading ? renderShadedOddRows(props, defaultRender) : renderDefaultRow(props, defaultRender);
    };

    const hasExpandableRows = onRenderExpandedRow !== undefined;
    const listLayoutMode = getLayoutMode(layoutMode);

    useEffect(() => {
        if (selectedKeys?.length === 0) {
            selection.setAllSelected(false);
        }
    }, [selectedKeys]);

    useEffect(() => {
        if (maintainSelection) {
            const selectionArray = selectedKeys ? selectedKeys.map((item) => item.key) : [];
            const newSelection = selection;

            for (const item of items) {
                if (selectionArray.includes(item.key)) {
                    newSelection.setKeySelected(item.key, true, false);
                }
            }

            setSelection(newSelection);
            itemsRef.current = items;
        }
    }, [items]);

    const expandItem = (item: { id: string }) => {
        setExpandedItemIds((prevItemKeys) => [...prevItemKeys, item.id]);
    };

    const contractItem = (item: { id: string }) => {
        setExpandedItemIds((prevItemKeys) => prevItemKeys.filter((prevItemKey) => prevItemKey !== item.id));
    };

    const renderExpandableRow = (rowProps?: IDetailsRowProps, defaultRender?: RenderFunction) => {
        ensure(rowProps);
        ensure(defaultRender);
        ensure(onRenderExpandedRow);

        if (!expandedItemIds.includes(rowProps?.item.id)) {
            return renderRow(rowProps, defaultRender);
        }

        return (
            <Stack>
                {renderRow(rowProps, () =>
                    defaultRender({
                        ...rowProps,
                        styles: { root: { backgroundColor: Theme.palette.themeLighterAlt } }
                    })
                )}
                <Stack
                    styles={{
                        root: { padding: 10 }
                    }}
                >
                    {onRenderExpandedRow(rowProps)}
                </Stack>
            </Stack>
        );
    };

    const expandDetailsColumn: IColumn = {
        key: "expand-details-key",
        minWidth: 15,
        maxWidth: 15,
        name: "",
        isResizable: false,
        onRender: (item) =>
            expandedItemIds.includes(item.id)
                ? renderContractButton(() => contractItem(item))
                : renderExpandButton(() => expandItem(item))
    };

    return (
        <FabricShimmeredDetailsList
            items={items}
            columns={hasExpandableRows ? [expandDetailsColumn, ...columns] : columns}
            selection={selection}
            selectionMode={selection.mode}
            layoutMode={listLayoutMode}
            constrainMode={ConstrainMode.unconstrained}
            isHeaderVisible={isHeaderVisible}
            enableShimmer={enableShimmer}
            shimmerLines={shimmerLines ?? 3}
            onRenderRow={hasExpandableRows ? renderExpandableRow : renderRow}
            onRenderItemColumn={onRenderItemColumn}
            onColumnHeaderClick={onColumnHeaderClick}
            onShouldVirtualize={onShouldVirtualize}
            {...(isStickyModeEnabled && getPropsForStickyMode())}
        />
    );
};

const renderExpandButton = (handleExpandItem: () => void) => (
    <Stack horizontalAlign="center">
        <ActionButton
            iconProps={{
                iconName: "ChevronDown"
            }}
            styles={{
                icon: {
                    fontSize: 12
                }
            }}
            onClick={handleExpandItem}
        />
    </Stack>
);

const renderContractButton = (handleContractItem: () => void) => (
    <Stack horizontalAlign="center">
        <ActionButton
            iconProps={{
                iconName: "ChevronUp"
            }}
            styles={{
                icon: {
                    fontSize: 12
                }
            }}
            onClick={handleContractItem}
        />
    </Stack>
);

const renderShadedOddRows = (rowProps?: IDetailsRowProps, defaultRender?: RenderFunction) => {
    ensure(defaultRender);

    if (rowProps && rowProps?.itemIndex % 2 === 0) {
        return renderShadedRow(rowProps, defaultRender);
    }

    return defaultRender(rowProps);
};

const renderShadedRow = (rowProps?: IDetailsRowProps, defaultRender?: RenderFunction) => {
    ensure(rowProps);
    ensure(defaultRender);

    const testProps = {
        "data-testid": "table-row",
        "data-testkey": rowProps.item.id,
        "data-testdisabled": rowProps.item.isDisabled
    };

    const rowMarkerStyle = rowProps.item.isModified ? `inset 3px 0px ${Theme.palette.themePrimary}` : undefined;
    const rowColors = rowProps.item.isDisabled
        ? {
              backgroundColor: Theme.semanticColors.disabledBackground,
              color: Theme.semanticColors.disabledText
          }
        : { backgroundColor: Theme.palette.themeLighterAlt };

    return defaultRender({
        ...rowProps,
        ...testProps,
        styles: { root: { boxShadow: rowMarkerStyle, ...rowColors } }
    });
};

const renderDefaultRow = (rowProps?: IDetailsRowProps, defaultRender?: RenderFunction) => {
    ensure(rowProps);
    ensure(defaultRender);

    const rowMarkerStyle = rowProps.item.isModified ? `inset 3px 0px ${Theme.palette.themePrimary}` : undefined;
    const rowColors = rowProps.item.isDisabled
        ? {
              backgroundColor: Theme.semanticColors.disabledBackground,
              color: Theme.semanticColors.disabledText
          }
        : { color: Theme.palette.neutralPrimaryAlt };

    const testProps = {
        "data-testid": "table-row",
        "data-testkey": rowProps.item.id,
        "data-testdisabled": rowProps.item.isDisabled
    };

    return defaultRender({
        ...rowProps,
        ...testProps,
        id: rowProps.item.id ? `table-item-${rowProps.item.id}` : undefined,
        styles: {
            root: {
                boxShadow: rowMarkerStyle,
                userSelect: "auto",
                ...rowColors
            }
        }
    });
};

const getPropsForStickyMode = () => {
    const gridStyles: Partial<IDetailsListStyles> = {
        root: {
            overflowX: "auto",
            selectors: {
                "& [role=grid]": {
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "start",
                    height: "calc(100vh - 430px)"
                }
            }
        },
        headerWrapper: {
            flex: "0 0 auto"
        },
        contentWrapper: {
            flex: "1 1 auto",
            overflow: "hidden"
        }
    };

    const classNames = mergeStyleSets({
        header: {
            margin: 0
        },
        row: {
            flex: "0 0 auto"
        },
        focusZone: {
            height: "100%",
            overflowY: "auto",
            overflowX: "hidden"
        },
        selectionZone: {
            height: "100%",
            overflow: "hidden"
        }
    });

    const focusZoneProps = {
        className: classNames.focusZone,
        "data-is-scrollable": "true"
    } as React.HTMLAttributes<HTMLElement>;

    return {
        layoutMode: DetailsListLayoutMode.fixedColumns,
        detailsListStyles: gridStyles,
        focusZoneProps: focusZoneProps,
        selectionZoneProps: { className: classNames.selectionZone }
    };
};
